import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import * as projectApi from 'api/project';
import { getJawByToothNumber } from 'lib/clinic/library';
import { genderList } from 'lib/mapper';
import moment from 'moment';
import { all, takeLatest } from 'redux-saga/effects';
import store from 'store';
import { FileActions, ProjectActions } from 'store/actionCreators';
import { createFetchAction, createSaga, fetchInitialState, fetchReducerActions } from 'store/utils';

export const init_project = createFetchAction('init_project');
export const fetch_projects = createFetchAction('fetch_projects');
export const fetch_project = createFetchAction('fetch_project');
export const create_project = createFetchAction('create_project');
export const edit_project = createFetchAction('edit_project');
export const delete_project = createFetchAction('delete_project');
export const edit_timeline = createFetchAction('edit_timeline');
export const fetch_rework_projects = createFetchAction('fetch_rework_projects');
export const fetch_rework_project = createFetchAction('fetch_rework_project');
export const fetch_copy_project = createFetchAction('fetch_copy_project');
export const fetch_project_files = createFetchAction('fetch_project_files');
export const fetch_project_histories = createFetchAction('fetch_project_histories');
export const create_project_history = createFetchAction('create_project_history');
export const create_project_sync = createFetchAction('create_project_sync');
export const edit_project_sync = createFetchAction('edit_project_sync');
export const reset_project_sync = createFetchAction('reset_project_sync');
export const edit_project_manager = createFetchAction('edit_project_manager');
export const edit_project_rework = createFetchAction('edit_project_rework');
export const fetch_partner_indication = createFetchAction('fetch_partner_indication');
export const fetch_chat_file = createFetchAction('fetch_chat_file');
export const upload_chat_file = createFetchAction('upload_chat_file');
export const edit_chat_file = createFetchAction('edit_chat_file');
export const delete_chat_file = createFetchAction('delete_chat_file');
export const clear_chat_all = createFetchAction('clear_chat_all');

interface InitialState {
  initProject: object | any;
  projects: object | any;
  project: object | any;
  createProject: object | any;
  editProject: object | any;
  deleteProject: object | any;
  editTimeline: object | any;
  reworkProjects: object | any;
  reworkProject: object | any;
  copyProject: object | any;
  projectFiles: object | any;
  projectHistories: object | any;
  createProjectHistory: object | any;
  createProjectSync: object | any;
  editProjectSync: object | any;
  resetProjectSync: object | any;
  editProjectManager: object | any;
  editProjectRework: object | any;
  excuteAppTimeline: object | any;
  hasAlarm: boolean;
  partnerIndication: object | any;
  fetchChatFile: object | any;
  uploadChatFile: object | any;
  editChatFile: object | any;
  deleteChatFile: object | any;
  clearChatAll: object | any;
}

const initialState: InitialState = {
  initProject: { ...fetchInitialState, data: null },
  projects: { ...fetchInitialState, data: null },
  project: { ...fetchInitialState, data: null },
  createProject: { ...fetchInitialState },
  editProject: { ...fetchInitialState },
  deleteProject: { ...fetchInitialState },
  editTimeline: { ...fetchInitialState },
  reworkProjects: { ...fetchInitialState, data: null },
  reworkProject: { ...fetchInitialState, data: null },
  copyProject: { ...fetchInitialState, data: null },
  projectFiles: { ...fetchInitialState, data: null },
  projectHistories: { ...fetchInitialState, data: null },
  createProjectHistory: { ...fetchInitialState, data: null },
  createProjectSync: { ...fetchInitialState },
  editProjectSync: { ...fetchInitialState },
  resetProjectSync: { ...fetchInitialState },
  editProjectManager: { ...fetchInitialState, data: null },
  editProjectRework: { ...fetchInitialState, data: null },
  excuteAppTimeline: {
    appName: null,
    progressFlag: null,
    projectCode: null,
  },
  hasAlarm: false,
  partnerIndication: { ...fetchInitialState, data: null },
  fetchChatFile: { ...fetchInitialState, data: null },
  uploadChatFile: { ...fetchInitialState, data: null },
  editChatFile: { ...fetchInitialState, data: null },
  deleteChatFile: { ...fetchInitialState, data: null },
  clearChatAll: { ...fetchInitialState, data: null },
};

const slice = createSlice({
  name: 'project',
  initialState,
  reducers: {
    ...fetchReducerActions(init_project, 'initProject'),
    ...fetchReducerActions(fetch_projects, 'projects'),
    set_projects: (
      state,
      { payload }: PayloadAction<{ projectCode: string; hasNewEvent: boolean; stage?: number }>,
    ) => {
      const { projectCode, hasNewEvent, stage } = payload;
      console.log('set_proejcts payload', payload);

      if (!Array.isArray(state.projects.data?.list)) return;
      state.projects.data.list = state.projects.data.list.map((project: any) => {
        if (project.projectCode === projectCode) {
          if (typeof stage === 'number') project.stage = stage;
          project.hasNewEvent = hasNewEvent;
        }

        return project;
      });
    },
    ...fetchReducerActions(fetch_project, 'project', {
      success: (state: any, { payload }: PayloadAction<any>) => {
        // console.log('fetch project action', payload);
        const { fileList, hasNewFile } = payload;
        state.projectFiles.data = {
          ...state.projectFiles.data,
          fileList,
          hasNewFile,
        };

        if (!payload?.projectInfo?.patientInfo) return;

        const {
          projectInfo: {
            patientInfo: { firstName, lastName, identification, gender, birthday },
          },
        } = payload;

        state.project.data.projectInfo.patientInfo = {
          ...state.project.data.projectInfo.patientInfo,
          listText: `${!!firstName ? firstName : ''} ${!!lastName ? lastName : ''}${
            !!identification ? identification : ''
          }, ${genderList[gender as keyof typeof genderList]}${
            moment(birthday).isValid() ? `, ${birthday}` : ''
          }`,
        };

        // 상하악 체크 업데이트 : 기존 상하악 옵션에 따라 situScan value를 활성화하였으나 상하악 옵션이 없어지면서 PIM 타입에서는 situScan의 value를 1로 고정
        // const {
        //   projectInfo: {
        //     isPIM = 0,
        //     indication: { teeth = [] },
        //   },
        // } = payload;

        // if (!isPIM) return;

        // const newTeeth = teeth.map((tooth: any) => {
        //   return {
        //     ...tooth,
        //     default: {
        //       ...tooth.default,
        //       situScan: {
        //         value: !!tooth?.implantLibrary ? 0 : 1,
        //         isSavedLast: false,
        //       },
        //     },
        //   };
        // });

        //////////////////////////////////////////////////////////////////

        // const upperJaw =
        //   teeth.filter((tooth: any) => getJawByToothNumber(tooth.number) === 'upper') || [];
        // const lowerJaw =
        //   teeth.filter((tooth: any) => getJawByToothNumber(tooth.number) === 'lower') || [];

        // const newTeeth = teeth.map((tooth: any) => {
        //   return {
        //     ...tooth,
        //     default: {
        //       ...tooth.default,
        //       situScanUpper: {
        //         value: upperJaw?.[0]?.default?.situScan?.value || 0,
        //         isSavedLast: false,
        //       },
        //       situScanLower: {
        //         value: lowerJaw?.[0]?.default?.situScan?.value || 0,
        //         isSavedLast: false,
        //       },
        //     },
        //   };
        // });

        // state.project.data.projectInfo.indication.teeth = newTeeth;
      },
    }),
    ...fetchReducerActions(create_project, 'createProject', {
      success(state: any, { payload }: PayloadAction<any>) {
        const { ipcRenderer } = window;

        if (!!ipcRenderer) {
          ipcRenderer.send('setLatestOnlineProjectList', {});
        }
      },
    }),
    ...fetchReducerActions(edit_project, 'editProject', {
      success(state: any, { payload }: PayloadAction<any>) {
        const { ipcRenderer } = window;

        if (!!ipcRenderer) {
          ipcRenderer.send('setLatestOnlineProjectList', {});
        }
      },
    }),
    ...fetchReducerActions(delete_project, 'deleteProject', {
      success(state: any, { payload }: PayloadAction<any>) {
        const { ipcRenderer } = window;

        if (!!ipcRenderer) {
          ipcRenderer.send('setLatestOnlineProjectList', {});
        }
      },
    }),
    ...fetchReducerActions(edit_timeline, 'editTimeline'),
    ...fetchReducerActions(fetch_rework_projects, 'reworkProjects'),
    ...fetchReducerActions(fetch_rework_project, 'reworkProject'),
    ...fetchReducerActions(fetch_copy_project, 'copyProject', {
      success: (state: any, { payload }: PayloadAction<any>) => {
        if (!payload?.projectInfo?.patientInfo) return;

        const {
          projectInfo: {
            patientInfo: { firstName, lastName, identification, gender, birthday },
          },
        } = payload;

        // const genderList: any = {
        //   M: 'Male',
        //   F: 'Female',
        //   N: 'None',
        // };

        state.copyProject.data.projectInfo.patientInfo = {
          ...state.copyProject.data.projectInfo.patientInfo,
          listText: `${!!firstName ? firstName : ''} ${!!lastName ? lastName : ''}${
            !!identification ? identification : ''
          }, ${genderList[gender as keyof typeof genderList]}${
            moment(birthday).isValid() ? `, ${birthday}` : ''
          }`,
        };
      },
    }),
    ...fetchReducerActions(fetch_project_files, 'projectFiles', {
      success(state: any, { payload }: PayloadAction<any>) {
        console.log('project projectFiles payload', payload);
      },
    }),
    ...fetchReducerActions(fetch_project_histories, 'projectHistories'),
    ...fetchReducerActions(create_project_history, 'createProjectHistory'),
    ...fetchReducerActions(create_project_sync, 'createProjectSync'),
    ...fetchReducerActions(edit_project_sync, 'editProjectSync'),
    ...fetchReducerActions(reset_project_sync, 'resetProjectSync'),
    ...fetchReducerActions(edit_project_manager, 'editProjectManager'),
    ...fetchReducerActions(edit_project_rework, 'editProjectRework'),
    ...fetchReducerActions(fetch_partner_indication, 'partnerIndication'),
    set_excuteapp_timeline: (state, { payload }) => {
      state.excuteAppTimeline.appName = payload.appName;
      state.excuteAppTimeline.progressFlag = payload.progressFlag;
      state.excuteAppTimeline.projectCode = payload.projectCode;
    },
    set_project_invite_alarm: (state, { payload }) => {
      state.hasAlarm = payload;
    },
    ...fetchReducerActions(fetch_chat_file, 'fetchChatFile'),
    ...fetchReducerActions(upload_chat_file, 'uploadChatFile'),
    ...fetchReducerActions(edit_chat_file, 'editChatFile'),
    ...fetchReducerActions(delete_chat_file, 'deleteChatFile'),
    ...fetchReducerActions(clear_chat_all, 'clearChatAll'),
  },
});

export const actions: any = slice.actions;

const handleCreateProjectHistory = () => {
  console.log('handleCreateProjectHistory');
  createSaga(actions, 'create_project_history', projectApi.createProjectHistory, {
    success() {
      ProjectActions.fetch_project_histories_request({
        projectCode: store.getState().project.project.projectCode,
        page: 1,
      });
    },
  });
};
export function* projectSaga() {
  yield all([
    takeLatest(
      actions.init_project_request,
      createSaga(actions, 'init_project', projectApi.initProject),
    ),
    takeLatest(
      actions.fetch_projects_request,
      createSaga(actions, 'fetch_projects', projectApi.fetchProjects, {
        isApiLoading: false,
      }),
    ),
    takeLatest(
      actions.fetch_project_request,
      createSaga(actions, 'fetch_project', projectApi.fetchProject, {
        success(data: any) {
          console.log('fetch_project data', data);
          FileActions.set_project_files(data);
          console.log('after set_project_files');
        },
      }),
    ),
    takeLatest(
      actions.create_project_request,
      createSaga(actions, 'create_project', projectApi.createProject),
    ),
    takeLatest(
      actions.edit_project_request,
      createSaga(actions, 'edit_project', projectApi.editProject),
    ),
    takeLatest(
      actions.delete_project_request,
      createSaga(actions, 'delete_project', projectApi.deleteProject),
    ),
    takeLatest(
      actions.edit_timeline_request,
      createSaga(actions, 'edit_timeline', projectApi.editTimeline),
    ),
    takeLatest(
      actions.fetch_rework_projects_request,
      createSaga(actions, 'fetch_rework_projects', projectApi.fetchReworkProjects),
    ),
    takeLatest(
      actions.fetch_rework_project_request,
      createSaga(actions, 'fetch_rework_project', projectApi.fetchCopyProject),
    ),
    takeLatest(
      actions.fetch_copy_project_request,
      createSaga(actions, 'fetch_copy_project', projectApi.fetchCopyProject),
    ),
    takeLatest(
      actions.fetch_project_files_request,
      createSaga(actions, 'fetch_project_files', projectApi.fetchProjectFiles),
    ),
    takeLatest(
      actions.fetch_project_histories_request,
      createSaga(actions, 'fetch_project_histories', projectApi.fetchProjectHistories),
    ),
    takeLatest(
      actions.create_project_history_request,
      createSaga(actions, 'create_project_history', projectApi.createProjectHistory, {
        success() {
          ProjectActions.fetch_project_histories_request({
            id: store.getState().project.project?.data?.projectInfo?.projectCode,
            data: { page: 1 },
          });
        },
      }),
    ),
    takeLatest(
      actions.create_project_sync_request,
      createSaga(actions, 'create_project_sync', projectApi.createProjectSync, {
        isAlertSuccess: true,
      }),
    ),
    takeLatest(
      actions.edit_project_sync_request,
      createSaga(actions, 'edit_project_sync', projectApi.editProjectSync, {
        isAlertSuccess: true,
      }),
    ),
    takeLatest(
      actions.reset_project_sync_request,
      createSaga(actions, 'reset_project_sync', projectApi.resetProjectSync, {
        isAlertSuccess: true,
      }),
    ),
    takeLatest(
      actions.edit_project_manager_request,
      createSaga(actions, 'edit_project_manager', projectApi.editProjectPartnerManager),
    ),
    takeLatest(
      actions.edit_project_rework_request,
      createSaga(actions, 'edit_project_rework', projectApi.editProjectRework),
    ),
    takeLatest(
      actions.fetch_partner_indication_request,
      createSaga(actions, 'fetch_partner_indication', projectApi.fetchPartnerIndication),
    ),
    takeLatest(
      actions.fetch_chat_file_request,
      createSaga(actions, 'fetch_chat_file', projectApi.fetchChatFile),
    ),
    takeLatest(
      actions.upload_chat_file_request,
      createSaga(actions, 'upload_chat_file', projectApi.uploadChatFile, { isApiLoading: false }),
    ),
    takeLatest(
      actions.edit_chat_file_request,
      createSaga(actions, 'edit_chat_file', projectApi.editChatFile, { isApiLoading: false }),
    ),
    takeLatest(
      actions.delete_chat_file_request,
      createSaga(actions, 'delete_chat_file', projectApi.deleteChatFile, { isApiLoading: false }),
    ),
    takeLatest(
      actions.clear_chat_all_request,
      createSaga(actions, 'clear_chat_all', projectApi.clearChatAll, { isApiLoading: false }),
    ),
  ]);
}

export default slice.reducer;
